import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Download, Clock, ArrowLeft, BookOpen, Sparkles, Bookmark, Share2,
  CheckCircle2, Tag, ArrowRight, Wand2,
} from "lucide-react";

export default function LibraryResource() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/library/${slug}`).then(({ data }) => setData(data)).catch(() => setData({ resource: null }))
      .finally(() => setLoading(false));
    if (user) {
      api.get("/library/saved").then(({ data: saves }) => {
        setSaved(saves.some(s => s.slug === slug));
      }).catch(() => {});
    }
  }, [slug, user]);

  const download = async () => {
    setDownloading(true);
    try {
      const { data: info } = await api.post(`/public/library/${slug}/download`);
      if (info.kind === "template") {
        // Real Excel template
        const backend = process.env.REACT_APP_BACKEND_URL;
        const link = document.createElement("a");
        link.href = `${backend}${info.download_url}`;
        link.click();
        toast.success("Download started");
      } else if (info.kind === "external") {
        window.open(info.download_url, "_blank");
      } else {
        toast.info("Preview only — sign up free to unlock the full resource");
        nav("/register");
      }
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const toggleSave = async () => {
    if (!user) { nav("/register"); return; }
    try {
      if (saved) {
        await api.delete(`/library/save/${slug}`);
        setSaved(false); toast.success("Removed from your library");
      } else {
        await api.post(`/library/save/${slug}`);
        setSaved(true); toast.success("Saved to your library");
      }
    } catch { toast.error("Could not update"); }
  };

  const share = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  if (loading) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (!data?.resource) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <BookOpen className="w-10 h-10 mx-auto text-muted-foreground" />
        <div className="font-display text-2xl mt-4">Resource not found</div>
        <Link to="/library"><Button className="mt-6 rounded-sm">Back to library</Button></Link>
      </div>
    );
  }

  const r = data.resource;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/library" className="text-sm hover:text-primary hidden md:inline">Library</Link>
            {user ? (
              <Link to="/app"><Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white">Open workspace</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" size="sm" className="rounded-sm">Sign in</Button></Link>
                <Link to="/register"><Button size="sm" className="rounded-sm bg-accent hover:bg-accent/90 text-white">Free account</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/library" className="inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to library
        </Link>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Left: content */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-primary text-primary-foreground">
                  {r.resource_type}
                </Badge>
                <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-secondary text-foreground">
                  {r.skill_level}
                </Badge>
                {r.time_saved && (
                  <Badge className="rounded-sm font-mono-plex text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800">
                    <Clock className="w-3 h-3 mr-1" /> Saves {r.time_saved}
                  </Badge>
                )}
              </div>
              <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-tight" data-testid="resource-title">
                {r.title}
              </h1>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{r.description}</p>
            </div>

            {/* Preview visual */}
            <div className="border border-border rounded-sm bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 h-56 flex items-center justify-center">
              <BookOpen className="w-14 h-14 text-primary/40" />
            </div>

            {/* Benefits */}
            {r.benefits?.length > 0 && (
              <div>
                <div className="overline text-primary mb-3">/ WHAT YOU GET</div>
                <ul className="space-y-2">
                  {r.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Preview content */}
            {r.preview_content && (
              <div>
                <div className="overline text-primary mb-3">/ PREVIEW</div>
                <div className="border border-border bg-card p-5 rounded-sm text-sm text-muted-foreground leading-relaxed font-mono-plex">
                  {r.preview_content}
                </div>
              </div>
            )}

            {/* AI upsell */}
            <div className="border border-accent/30 bg-accent/5 rounded-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-accent text-white grid place-items-center shrink-0">
                  <Wand2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">Customize this with AI</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Open this in the RuleAI workspace and use Claude Sonnet 4.6 to rewrite it for your company,
                    convert it into an SOP, translate it into other languages, or export to Word, PDF, or Excel.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono-plex uppercase tracking-widest">
                    <span className="px-2 py-1 bg-card border border-border rounded-sm">◆ Rewrite for my company</span>
                    <span className="px-2 py-1 bg-card border border-border rounded-sm">◆ Convert to SOP</span>
                    <span className="px-2 py-1 bg-card border border-border rounded-sm">◆ Translate</span>
                    <span className="px-2 py-1 bg-card border border-border rounded-sm">◆ Export to Word/PDF</span>
                  </div>
                  <Link to="/register">
                    <Button size="sm" className="mt-4 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="resource-ai-upsell">
                      Unlock AI customization — free <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Related */}
            {data.related?.length > 0 && (
              <div>
                <div className="overline text-primary mb-3">/ RELATED</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {data.related.map((rel) => (
                    <Link key={rel.slug} to={`/library/${rel.slug}`}
                          className="border border-border bg-card p-4 rounded-sm hover:border-primary transition-colors"
                          data-testid={`related-${rel.slug}`}>
                      <div className="font-medium text-sm">{rel.title}</div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{rel.description}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: sticky action panel */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="border border-border bg-card p-6 rounded-sm space-y-3">
                <Button
                  onClick={download}
                  disabled={downloading}
                  className="w-full h-12 rounded-sm bg-accent hover:bg-accent/90 text-white"
                  data-testid="resource-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> {downloading ? "Preparing…" : (r.template_id ? "Download .xlsx" : "Get this resource")}
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={toggleSave} variant="outline" size="sm" className="rounded-sm" data-testid="resource-save-btn">
                    <Bookmark className={`w-3.5 h-3.5 mr-1.5 ${saved ? "fill-current" : ""}`} /> {saved ? "Saved" : "Save"}
                  </Button>
                  <Button onClick={share} variant="outline" size="sm" className="rounded-sm" data-testid="resource-share-btn">
                    <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
                  </Button>
                </div>
                <div className="pt-3 border-t border-border text-xs text-muted-foreground space-y-1 font-mono-plex uppercase tracking-widest">
                  <div className="flex justify-between"><span>Downloads</span><span className="text-foreground">{r.downloads_count || 0}</span></div>
                  <div className="flex justify-between"><span>Views</span><span className="text-foreground">{r.views_count || 0}</span></div>
                </div>
              </div>

              {r.tags?.length > 0 && (
                <div className="border border-border bg-card p-6 rounded-sm">
                  <div className="overline text-muted-foreground mb-3">Tags</div>
                  <div className="flex flex-wrap gap-1.5">
                    {r.tags.map((t) => (
                      <span key={t} className="text-[10px] font-mono-plex uppercase tracking-widest px-2 py-1 bg-secondary rounded-sm">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
